/**
 * reCAPTCHA Enterprise (score-based, same as v3).
 * Must match livitalk-ui: enterprise.js + grecaptcha.enterprise.execute.
 * Do not use legacy api.js — Google returns BROWSER_ERROR for this auth API.
 */
export const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6Lehn-MqAAAAAA6-PYVqBJE6y8KFRd4NZZjgfT4K';

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (cb: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

function loadRecaptchaScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha?.enterprise) {
      resolve();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-recaptcha-enterprise]'
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Failed to load reCAPTCHA')),
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.dataset.recaptchaEnterprise = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
    document.head.appendChild(script);
  });
}

/**
 * Mint a fresh Enterprise token for the given action (backend validates score).
 */
export async function executeRecaptcha(action: string = 'login'): Promise<string> {
  await loadRecaptchaScript();

  return new Promise((resolve, reject) => {
    if (!window.grecaptcha?.enterprise) {
      reject(new Error('reCAPTCHA unavailable'));
      return;
    }

    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha!.enterprise.execute(RECAPTCHA_SITE_KEY, {
          action,
        });
        if (!token) {
          reject(new Error('Empty captcha token'));
          return;
        }
        resolve(token);
      } catch (err) {
        reject(err);
      }
    });
  });
}
